import { Button, MiniLoader, Icon } from '@pwc/pwc-deals-ui';
import { getUserSettingsSelector } from 'common/redux/userSettings/userSettingsSlice';
import { clearLibraryItems } from 'features/contentLibrary/libraryItemService';
import { getProjectId } from 'features/project/projectSlice';
import { MouseEvent, useRef, useState } from 'react';
import { Overlay, Popover, Toast } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AutoSyncIcon } from '../../assets/icons/icon-autosync.svg';
import './RefreshProject.scss';

const RefreshProject = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [toast, setToast] = useState({
    isShowToast: false,
    isTypeError: false,
  });
  const [isLoadingManualRefresh, setIsLoadingManualRefresh] = useState(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  const projectId = useSelector(getProjectId);
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userSettings = useSelector(getUserSettingsSelector);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const darkMode = !!userSettings?.settings?.darkMode || false;

  const ref = useRef(null);

  const handleClick = async () => {
    if (isLoadingManualRefresh) {
      return;
    }
    setIsLoadingManualRefresh(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const clearLibraryItemsResponse = await clearLibraryItems(projectId);
      setToast({
        isShowToast: true,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        isTypeError: clearLibraryItemsResponse?.errors?.length > 0,
      });
    } catch (err) {
      // TODO: provide proper error indicator to user
    }
    setIsLoadingManualRefresh(false);
  };

  return (
    <div
      className="manual-refresh-wrapper"
      data-testid="manual-refresh-wrapper"
      ref={ref}
    >
      <Button
        className="usersnippet-initials-text button-no-appearance"
        data-testid="manual-refresh-button"
        onClick={handleClick}
        onMouseEnter={(event: MouseEvent<HTMLElement>) => {
          setIsVisible(true);
          setTarget(event.currentTarget);
        }}
        onMouseLeave={() => setIsVisible(false)}
        type="button"
        iconClasses={undefined}
        disabled={false}
        icon=""
      >
        {isLoadingManualRefresh ? (
          <MiniLoader dataTestId="manual-refresh-loader" />
        ) : (
          <AutoSyncIcon />
        )}
      </Button>
      <Overlay
        show={isVisible}
        target={target}
        placement="bottom"
        rootClose
        onHide={() => {
          setIsVisible(!isVisible);
        }}
        container={ref.current}
      >
        <Popover
          id="popover-manual-refresh"
          ref={ref}
          data-testid="popup-manual-refresh"
          className={darkMode ? 'theme-dark' : 'theme-light'}
        >
          <Popover.Content className="popup-user">
            <div className="manual-refresh-toolbar-section">
              <span
                className={`${!darkMode ? 'emphasis-light' : 'emphasis-dark'}`}
              >
                {isLoadingManualRefresh ? t('refreshing') : t('refresh')}
              </span>
              <span>{isLoadingManualRefresh ? '' : t('autoSyncOff')}</span>
            </div>
            <div className="manual-refresh-button-info">
              {isLoadingManualRefresh
                ? t('refreshingSharePointData')
                : t('refreshSharePointData')}
            </div>
          </Popover.Content>
        </Popover>
      </Overlay>
      <Toast show={toast?.isShowToast} className="toast-container">
        <Toast.Header
          closeButton={false}
          className="toast-container--toast-header"
        >
          {toast?.isTypeError ? (
            <span>
              <Icon name="alert" className="icon-error fill" width="24" />
              <span className="toast-container--toast-header-error-text">
                {t('refreshFailed')}
              </span>
            </span>
          ) : (
            <div className="toast-container--toast-header-text">
              {t('refreshComplete')}
            </div>
          )}
        </Toast.Header>
        <Toast.Body className="toast-container--toast-body">
          {toast?.isTypeError ? (
            <div className="toast-container--toast-body-text">
              {t('latestSharePointData')}
            </div>
          ) : (
            <div className="toast-container--toast-body-text">
              {t('refreshCompleteMessage')}
            </div>
          )}
          <hr className="toast-container--toast-body--toast-body-divider" />
          {toast?.isTypeError ? (
            <Button
              className="button button-small toast-container--toast-body--retry"
              id="btn-error-retry"
              data-testid="header-error-retry"
              onClick={handleClick}
              aria-label="error-retry"
              icon={undefined}
              iconClasses={undefined}
            >
              {t('retry')}
            </Button>
          ) : (
            <div className="toast-container--toast-body--buttons">
              <Button
                className="button button-small toast-container--toast-body--reload-page"
                id="btn-reload-page"
                data-testid="header-reload-page"
                onClick={() => history.go(0)}
                aria-label="reload-page"
                icon={undefined}
                iconClasses={undefined}
              >
                {t('reloadPage')}
              </Button>
              <Button
                className="button button-outline toast-container--toast-body--reload-later"
                id="btn-reload-later"
                data-testid="header-reload-later"
                onClick={() => setToast({ ...toast, isShowToast: false })}
                aria-label="reload-later"
                icon={undefined}
                iconClasses={undefined}
              >
                {t('reloadLater')}
              </Button>
            </div>
          )}
        </Toast.Body>
      </Toast>
    </div>
  );
};

export default RefreshProject;
